import React from 'react';
import * as FaIcons from 'react-icons/fa';
import SafeIcon from '../../common/SafeIcon';

const GenderIcon = ({ 
  gender, 
  petType = 'dog',
  size = 'md',
  className = '',
  showBackground = true 
}) => {
  const { FaMars, FaVenus, FaQuestion } = FaIcons;

  // 尺寸配置
  const sizeClasses = {
    sm: 'w-6 h-6',
    md: 'w-8 h-8', 
    lg: 'w-10 h-10',
    xl: 'w-12 h-12'
  };

  // 性別符號尺寸和位置
  const genderSymbolClasses = {
    sm: 'w-3 h-3 text-[8px] -top-0.5 -right-0.5',
    md: 'w-3.5 h-3.5 text-[9px] -top-0.5 -right-0.5',
    lg: 'w-4 h-4 text-[10px] -top-1 -right-1',
    xl: 'w-5 h-5 text-xs -top-1 -right-1'
  };

  // 寵物圖標尺寸 - 使用與表單一致的 emoji
  const petEmojiClasses = {
    sm: 'text-sm',
    md: 'text-lg',
    lg: 'text-xl',
    xl: 'text-2xl'
  };

  // 確保 gender 和 petType 存在並轉換為小寫
  const normalizedGender = gender ? String(gender).toLowerCase().trim() : '';
  const normalizedPetType = petType ? String(petType).toLowerCase().trim() : 'dog';
  
  // 判斷性別
  const isMale = normalizedGender === 'male' || normalizedGender === '公' || normalizedGender === 'boy' || normalizedGender === 'm';
  const isFemale = normalizedGender === 'female' || normalizedGender === '母' || normalizedGender === 'girl' || normalizedGender === 'f';
  const hasGender = isMale || isFemale;
  
  // 設定性別相關的顏色
  let genderStyle;
  if (isMale) {
    genderStyle = {
      borderColor: 'border-blue-400 dark:border-blue-500',
      bgColor: showBackground ? 'bg-gradient-to-br from-blue-100 via-blue-50 to-cyan-100 dark:from-blue-900/40 dark:to-cyan-800/40' : 'bg-gray-100 dark:bg-gray-700',
      iconBg: 'bg-blue-500',
      genderIcon: FaMars
    };
  } else if (isFemale) {
    genderStyle = {
      borderColor: 'border-pink-400 dark:border-pink-500',
      bgColor: showBackground ? 'bg-gradient-to-br from-pink-100 via-pink-50 to-rose-100 dark:from-pink-900/40 dark:to-rose-800/40' : 'bg-gray-100 dark:bg-gray-700',
      iconBg: 'bg-pink-500',
      genderIcon: FaVenus
    };
  } else {
    // 未指定性別 - 使用中性顏色
    genderStyle = {
      borderColor: 'border-gray-400 dark:border-gray-500',
      bgColor: showBackground ? 'bg-gradient-to-br from-gray-100 via-gray-50 to-slate-100 dark:from-gray-900/40 dark:to-slate-800/40' : 'bg-gray-100 dark:bg-gray-700',
      iconBg: 'bg-gray-500',
      genderIcon: FaQuestion
    };
  }

  // 根據寵物種類選擇 emoji - 與 PetForm 保持一致
  let petEmoji;
  switch (normalizedPetType) {
    case 'dog':
    case '狗':
    case '狗狗':
      petEmoji = '🐕';
      break;
    case 'cat':
    case '貓':
    case '貓咪':
      petEmoji = '🐱';
      break;
    case 'bird':
    case '鳥':
    case '鳥類':
      petEmoji = '🦜';
      break;
    case 'fish':
    case '魚':
    case '魚類':
      petEmoji = '🐠';
      break;
    case 'rabbit':
    case '兔子':
    case '兔':
      petEmoji = '🐰';
      break;
    case 'hamster':
    case '倉鼠':
    case '倉鼠類':
      petEmoji = '🐹';
      break;
    case 'turtle':
    case '烏龜':
    case '龜':
      petEmoji = '🐢';
      break;
    case 'other':
    case '其他':
    default:
      petEmoji = '🐾';
      break;
  }

  return (
    <div 
      className={`
        ${sizeClasses[size]} 
        ${genderStyle.bgColor}
        ${showBackground ? `border-2 ${genderStyle.borderColor}` : ''} 
        rounded-full flex items-center justify-center relative group 
        hover:scale-110 transition-all duration-300 shadow-lg 
        flex-shrink-0
        ${className}
      `}
    >
      {/* 主要寵物 emoji */}
      <span className={`${petEmojiClasses[size]} select-none`}>
        {petEmoji}
      </span>
      
      {/* 右上角的性別符號 - 只有在有性別時才顯示 */}
      {hasGender && (
        <div 
          className={`
            absolute rounded-full flex items-center justify-center
            ${genderStyle.iconBg} border-2 border-white dark:border-gray-800 shadow-md
            text-white
            ${genderSymbolClasses[size]}
            transform transition-transform duration-200 hover:scale-110
          `}
        >
          <SafeIcon icon={genderStyle.genderIcon} className="text-white" />
        </div>
      )}
    </div>
  );
};

export default GenderIcon;